import React from 'react';
import Button from '../ui/Button';
import { OPEN_IN_DEVICES_CAP } from './deviceListSelection';

/**
 * FS-882 — the bar above the Device List while anything is selected: the
 * count, Open in Devices and Clear selection, plus the "select all matching"
 * offer when the whole page is selected and the list runs past it.
 *
 * The live region is always mounted (a region that appears together with its
 * text is not reliably announced), so the count is read out as it changes.
 */

export interface SelectionBarProps {
  count: number;
  /** Selected devices that are not on the page shown. */
  notShown: number;
  onOpen: (e: React.MouseEvent) => void;
  onClear: () => void;
  /** Rows on the page shown. */
  pageRowCount: number;
  /** Devices the current search and filters match, across every page. */
  matchingTotal: number;
  /** Every row on the page is selected. */
  pageAllSelected: boolean;
  /** "Select all matching" ran for this view and all of it is still selected. */
  allMatchingSelected: boolean;
  /** How many it fetched: every match, or the first OPEN_IN_DEVICES_CAP. */
  allMatchingCount: number;
  matchingBusy: boolean;
  matchingError: string | null;
  onSelectAllMatching: () => void;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

const SelectionBar: React.FC<SelectionBarProps> = ({
  count,
  notShown,
  onOpen,
  onClear,
  pageRowCount,
  matchingTotal,
  pageAllSelected,
  allMatchingSelected,
  allMatchingCount,
  matchingBusy,
  matchingError,
  onSelectAllMatching,
}) => {
  const overCap = count > OPEN_IN_DEVICES_CAP;
  const listRunsPastPage = matchingTotal > pageRowCount;

  const openTitle = overCap
    ? `Open the first ${OPEN_IN_DEVICES_CAP} of the ${count} selected devices on the Devices page, in this list's order (one link carries at most ${OPEN_IN_DEVICES_CAP}). Previous and Next there step through them one at a time.`
    : `Open the ${count} selected ${plural(count, 'device', 'devices')} on the Devices page, in this list's order. Previous and Next there step through them one at a time.`;
  const clearTitle =
    notShown > 0
      ? `Unselect all ${count} devices, including the ${notShown} not shown on this page`
      : `Unselect the ${count} selected ${plural(count, 'device', 'devices')}`;

  // The "select all matching" line: only while the whole page is selected
  // and the list runs past it, or once every matching device is selected.
  // Above the cap it offers, and selects, the first OPEN_IN_DEVICES_CAP: one
  // trip to Devices carries no more.
  const matchCapped = matchingTotal > OPEN_IN_DEVICES_CAP;
  let matchingLine: React.ReactNode = null;
  if (allMatchingSelected && listRunsPastPage) {
    matchingLine = (
      <>
        <span>
          {allMatchingCount < matchingTotal
            ? `The first ${allMatchingCount} of ${matchingTotal} matching devices are selected.`
            : `All ${allMatchingCount} matching devices are selected.`}
        </span>
        <Button variant="link" onClick={onClear} title={clearTitle}>
          Clear selection
        </Button>
      </>
    );
  } else if (pageAllSelected && listRunsPastPage) {
    matchingLine = (
      <>
        <span>
          All {pageRowCount} on this page {plural(pageRowCount, 'is', 'are')} selected.
        </span>
        <Button
          variant="link"
          onClick={onSelectAllMatching}
          disabled={matchingBusy}
          aria-busy={matchingBusy || undefined}
          title={
            matchCapped
              ? `Devices opens ${OPEN_IN_DEVICES_CAP} at a time, so this selects the first ${OPEN_IN_DEVICES_CAP} of the ${matchingTotal} devices that match the current search and filters, in this list's order`
              : `Fetch every device that matches the current search and filters (${matchingTotal}), not just this page, and select them all in this list's order`
          }
        >
          {matchCapped
            ? matchingBusy
              ? `Selecting the first ${OPEN_IN_DEVICES_CAP} matching devices…`
              : `Select the first ${OPEN_IN_DEVICES_CAP} matching devices`
            : matchingBusy
              ? `Selecting all ${matchingTotal} matching devices…`
              : `Select all ${matchingTotal} matching devices`}
        </Button>
      </>
    );
  }

  return (
    <>
      <span role="status" aria-live="polite" className="sr-only">
        {count > 0 ? `${count} selected` : 'No devices selected'}
      </span>
      {count > 0 && (
        <div
          className="mt-3 rounded-sm border border-status-info-border bg-blue-50 px-3 py-2 text-sm text-blue-900"
          data-testid="selection-bar"
        >
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span>
              <span className="font-medium">{count} selected</span>
              {notShown > 0 && (
                <span title="Selected devices that are not on this page: on another page, or hidden by the current search or filters. They stay selected, and Open in Devices lists them after the ones shown here.">
                  {' '}
                  · {notShown} not shown
                </span>
              )}
            </span>
            <Button variant="primary" size="sm" onClick={onOpen} title={openTitle}>
              Open in Devices
            </Button>
            <Button variant="secondary" size="sm" onClick={onClear} title={clearTitle}>
              Clear selection
            </Button>
            {overCap && (
              <span className="text-xs">
                Devices opens the first {OPEN_IN_DEVICES_CAP} of the {count} selected, in this
                list's order.
              </span>
            )}
          </div>
          {matchingLine && (
            <div className="mt-1 flex flex-wrap items-center gap-x-2" data-testid="select-all-matching">
              {matchingLine}
            </div>
          )}
          {matchingError && (
            <p role="alert" className="mt-1 text-red-700">
              Could not select all matching devices: {matchingError}
            </p>
          )}
        </div>
      )}
    </>
  );
};

export default SelectionBar;
